import { useState, useReducer, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAtom } from "jotai";
import { projectAtomList } from "../../../atoms";
import userScope from "../../CustomHooks/userScope";
import useProjectTypeValue from "../../../utils/useCurrentProjectValue";

import {
  initialCBAssetList,
  CBAssetListReducer,
} from "../Reducers/CBAssetListReducer";
import {
  initialCBEVList,
  CBEVListReducer,
} from "../Reducers/CBEVListReducer";
import {
  initialCBLoadList,
  CBLoadListReducer,
} from "../CBLoadListReducer";
import {
  initialPVDetails,
  PVDetailsReducer,
} from "../Reducers/PVDetailsReducer";
import { initialCBGTList, CBGTListReducer } from "../Reducers/CBGTListReducer";
import {
  initialCount,
  countReducer,
} from "../Reducers/CountReducer";
import {
  initialTypes,
  projectKindReducer,
} from "../Reducers/projectKindReducer";
import {
  ComponentDataReducer,
  initialComponentData,
} from "../Reducers/ComponentDataReducer";
import { getProjectId } from "../../../utils/project";

const useDesignAssetState = (props) => {
  const projectTypeValue = useProjectTypeValue();
  const dispatch = useDispatch();
  const UserScope = userScope();

  // Basic state
  const [loading, setLoading] = useState(false);
  const [displayPage, setDisplayPage] = useState(false);
  const [sldLoadCompleted, setSldLoadCompleted] = useState(false);
  const [useCaseValidGenset, setUseCaseValidGenset] = useState(false);
  const [deleteOneDER, setDeleteOneDER] = useState(false);
  const [defaultConfigurationStatus, setDefaultConfigurationStatus] =
    useState(false);
  const [siteCreated, setSiteCreated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [siteType, setSiteType] = useState("");
  const [evceDiagramChangeRequired, setEvceDiagramChangeRequired] =
    useState(false);
  const [isAllowMultipleLoads, setIsAllowMultipleLoads] = useState(false);
  const [isExportOptimization, setIsExportOptimization] = useState(false);
  const [switchboardType, setSwitchboardType] = useState("");
  const [clientType, setClientType] = useState("");
  const [architectureType, setArchitectureType] = useState(null);
  const [nominalVoltage, setNominalVoltage] = useState("");
  const [isSelectSpanishCode, setIsSelectSpanishCode] = useState(false);
  const [defaultPreFillData, setDefaultPreFillData] = useState([]);
  const [availableDer, setAvailableDer] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const { projectType } = useSelector((state) => state.projectType);
  const [isEvce, setIsEvce] = useState(false);
  const [prevPvGroupNames, setPrevPvGroupNames] = useState([]);

  // Communication data
  const [batteryCommunicationData, setBatteryCommunicationData] = useState({
    ipaddress: "",
    subnetmask: "",
    defaultGateway: "",
    modbusSlaveId: "",
  });
  const [evCommunicationData, setEvCommunicationData] = useState({
    ipaddress: "",
    subnetmask: "",
    defaultGateway: "",
    modbusSlaveId: "",
  });

  const [powerMeter, setPowerMeter] = useState({
    assetId: "",
    assetType: "Power Meter",
    manufacturer: "",
    communicationModel: "PM3250",
    imageLink: null,
    assetInfo: "",
    assetEnabled: false,
    assetProperties: [],
    assetAttributes: [],
    comments: null,
  });

  const [communicationState, setCommunicationState] = useState({
    ifeIpAddress1: "",
    ifeIpAddress2: "",
    ifeIpAddress3: "",
    ifeIpAddress4: "",
    ifeIpAddress5: "",
    ifeIpAddress6: "",
    ifeIpAddress7: "",
    ifeIpAddress8: "",
    ifeIpAddress9: "",
    ifeIpAddress10: "",
    ifeIpAddress11: "",
    ifeIpAddress12: "",
    ifeIpAddress13: "",
    ifeIpAddress14: "",
    ifeIpAddress15: "",
    ifeIpAddress16: "",
    ifeIpAddress17: "",
    ifeIpAddress18: "",
    ifeIpAddress19: "",
    ifeIpAddress20: "",
    ifeIpAddress21: "",
    ifeIpAddress22: "",
    ifeIpAddress23: "",
    ifeIpAddress24: "",
    ifeSubnetMask: "",
    ifeSubnetMask2: "",
    ifeSubnetMask3: "",
    ifeSubnetMask4: "",
    ifeGateway: "",
    ifeGateway2: "",
    ifeGateway3: "",
    ifeGateway4: "",
  });
  const [cbCommunicationValues, setCbCommunicationValues] = useState({
    allIps: [],
    availableIps: [],
    usedIps: [],
    ifeIpSets: {},
  });

  // Details block visibility
  const [detailsBlock, setDetailsBlock] = useState({
    cbAssetDetailsBlock: false,
    cbLoadDetailsBlock: false,
    cbEvDetailsBlock: false,
    loadDetailsBlock: false,
    evDetailsBlock: false,
    gtDetailsBlock: false,
    assetUtilityDetailsBlock: false,
    assetGensetDetailsBlock: false,
    assetBatteryDetailsBlock: false,
    assetPvDetailsBlock: false,
    assetCHPDetailsBlock: false,
    assetHybridDetailsBlock: false,
    newSBCbAssetDetailsBlock: false,
    cbAssetDetailsBlockA2E: false,
    cbAssetDetailsBlockGrid: false,
    pmAssetDetailsBlockA2E: false,
    pmAssetDetailsBlockGrid: false,
    existingPmAssetDetailsBlockGrid: false,
    hardWiredCbDetailsBlockGrid: false,
    assetBessHybridDetailsBlock: false,
  });

  // Selection state
  const [selectedValues, setSelectedValues] = useState({
    selectedAsset: "",
    selectedAssetCB: "",
    selectedAssetPM: "",
    selectedLoad: "",
    selectedLoadPM: "",
    selectedLoadCB: "",
    selectedEv: "",
    selectedEvCB: "",
    selectedEvPM: "",
    selectedGt: "",
    selectedGtCB: "",
    selectedPv: "",
    selectSubPv: "",
  });

  // Config data
  const [configData, setConfigData] = useState({
    utilityConfigData: {
      name: "",
      description: "",
      utilityCO2: "",
      maximum_available_power: 10,
      co2_emission_rate_type: "Fixed",
      co2_emission_rate: "",
      co2Provider: "Watt Time",
      co2_emission_unit: "gram(g)",
    },
    gensetConfigData: {
      name: "",
      description: "",
      min_power: "",
      max_power: "",
      gensetCO2: "",
      starting_timer: "",
      cooling_timer: "",
      minimum_running_period: "",
    },
    batteryConfigData: {
      name: "",
      description: "",
      min_power: "",
      max_power: "",
      capacity: 0,
      soc_max: 90,
      soc_min: 10,
      SoC_min_optimization: 0,
      soc_nominal_power: 60,
      SoC_fallback: 60,
      starting_timer: "",
      quantityOfBess: 1,
      perUnitMaxPower: 0,
      perUnitMaxEnergy: 0,

      charge_rate:0,
      discharge_rate:0,
      flag_curtail_power:false,
      min_power_rate:0,
      nbr_steps_for_curtailment:0,
      weight_early_charge:0,
      weight_soc_bounds_violation:0, 
      weight_delta_soc_offgrid_violation:0,
      min_frequency_reserve_duration:0,
      aging:"",
      participant_type: "",
      system_type: "",
      type: "",
    },
    loadConfigData: {
      name: "",
      description: "",
      maximum_available_power: 0,
    },
    evConfigData: {
      name: "",
      description: "",
      maximum_available_power: 0,
    },
    gtConfigData: {
      name: "",
      description: "",
    },
    cbConfigData: {
      name: "",
      description: "",
      motorized: true,
      contactor: false,
      loadCategory: 0,
      loadPriority: 0,
    },
    contactorConfigData: {
      name: "",
      description: "",
      loadCategory: 0,
      loadPriority: 0,
    },
    sampleContactorConfigData: {
      name: "",
      description: "",
      loadCategory: 0,
      loadPriority: 0,
    },
    cbConfigDataSample: {
      name: "",
      description: "",
      motorized: true,
      loadCategory: 0,
      loadPriority: 0,
    },
    sampleCbConfigData: {
      name: "",
      description: "",
      motorized: true,
      loadCategory: 0,
      loadPriority: 0,
    },
    sampleConfigData: {
      name: "",
      description: "",
      min_power: "",
      max_power: "",
    },
    configData: {
      name: "",
      description: "",
      min_power: "",
      max_power: 62.5,
    },
    powerMeterData: {
      name: "",
      description: "",
      powerMeterModbusId: "",
    },
    chpConfigData: {
      name: "",
      description: "",
      nominal_power: "",
    },
    hybridConfigData: {
      capacity: 0,
      name: "",
      description: "",
      pcu_ac_rated_power: "",
      storage_capacity: "",
      model: "",
      max_capacity: "",
      max_solar_power: "",
      max_inverter_output_power: "",
      corresponding_name_plate_capacity_value: "",
      corresponding_name_plate_capacity: [],
      hybridModels: [],
      name_plate_energy: [],
      storage_capacity_hr: [],
      time_capacity: "",
    },
  });

  const [isLoadNameUnique, setIsLoadNameUnique] = useState(false);
  const [gsxExternalId, setGsxExternalId] = useState({
    utilityExternalId: "",
    gensetExternalId: "",
    pvAssetExternalId: "",
    batteryExternalId: "",
  });
  const [isBessController, setIsBessController] = useState(false);
  const [assetDetailsDisplay, setAssetDetailsDisplay] = useState({
    assetId: "",
    assetName: "",
    assetType: "",
    manufacturer: "",
    assetInfo: "",
    communicationModel: "",
    imageLink: "",
    assetProperties: [],
  });
  const [assetInfo, setAssetInfo] = useState({
    assetSelected: false,
    manufacturerLoading: false,
    assets: [],
    assetManufacturer: [],
    assetModelType: [],
    selectedManufacturer: "",
    selectdModelType: "",
  });
  const [bessControllerPrevValue, setBessControllerPrevValue] = useState({
    max_power: "",
    capacity: "",
    quantityOfBess: 0,
  });
  const [cbDer, setCbder] = useState("");
  const [CBValues, setCBValues] = useState({});
  const [defaultsMinMax, setDefaultsMinMax] = useState({});
  const [coordinates, setCoordinates] = useState({
    xDer: 8,
    xDerGrid: 370,
    yDerGrid: 141,
    xDerGridPM: 495,
    yDerGridPM: 127.5,
    xDerCBDiff: 590,
    yDerCBDiff: 269,
    xDerCB: 108,
    derDiff: 110,
    derDiffGrid: 120,
    xDerPvHyb: 380,
    xDerCBPvHyb: 480,
    xDerPMPvHyb: 480,
    derDiffPvHyb: 120,
    xLoad: 10,
    xLoadCB: 55,
    xLoadEM: 446,
    xLoadNew: 65,
    xLoadCBNew: 110,
    xLoadPM: 345,
    xLoadPMNew: 345,
    loadDiff: 130,
    xAxisLoadCBDiff: 55,
    cbEV: {
      xDerEV: 529.5,
      yDerEV: 488,
      xDerPM: 480,
      yDerPM: 407.5,
      x1DerEVCB: 555,
      y1DerEVCB: 463,
      x2DerEVCB: 555,
      y2DerEVCB: 367,
    },
    ecc: { width: 690 },
    busbar: { x2: 755 },
    eccwidth: 208,
    busbarx2: 208,
    CX: 600,
    CY: 350,
    radius: 46,
    angle: 0,
    busbarCordinates: {
      x11: 168,
      y11: 335,
      x21: 408,
      y21: 335,
      x12: 650,
      y12: 425,
      x22: 890,
      y22: 425,
    },
  });
  const [rectangleWidth, setRectangleWidth] = useState(null);
  const [snackBar, setSnackBar] = useState({ type: "", message: "" });
  const [alert, setAlert] = useState({
    message: "",
    alertPopUpStatus: false,
    title: "",
    isRoute: false,
    routePath: "",
  });
  const [confirm, setConfirm] = useState({
    confirmMessage: "",
    confirmPopupStatus: false,
  });
  const [remoteConfirm, setRemoteConfirm] = useState({
    confirmMessage: "",
    confirmPopupStatus: false,
  });
  const [duplicateUpdate, setDuplicateUpdate] = useState({
    der: "",
    load: "",
    pv: "",
    ev: "",
  });
  const [runCalculateCoordinates, setRunCalculateCoordinates] = useState({
    componentPush: false,
  });
  const [runGenerateComponentsBlock, setRunGenerateComponentsBlock] = useState({
    generateComponentsBlock: false,
  });
  const [batteryRack, setBatteryRack] = useState("1");
  const [batterySystemA2EConfig, setBatterySystemA2EConfig] = useState({
    model: [],
    namePlateEnergy: [],
  });
  const [isOpenLoadConfig, setIsOpenLoadConfig] = useState(false);
  const [isOpenGtConfig, setIsOpenGtConfig] = useState(false);
  const [loadSide, setLoadSide] = useState("LINE_SIDE");
  const [isAntiIslandingRelayEnable, setIsAntiIslandingRelayEnable] =
    useState(false);
  const [gridTransitionOnline, setGridTransitionOnline] = useState(false);
  const [defaultCbData, setDefaultCbData] = useState({});
  const [defaultPmData, setDefaultPmData] = useState({});
  const [defaultDummyCbData, setDefaultDummyCbData] = useState({});
  const [gensetAssetData, setGensetAssetData] = useState({});
  const [evContactorData, setEVContactorData] = useState({});
  const [gTContractorData, setGTContractorData] = useState({});
  const [defaultGTData, setGTDefaultData] = useState({});
  const [evDefaultData, setEVDefaultData] = useState({});
  const [evPMData, setEVPMData] = useState({});
  const [jsonData, setJsonData] = useState({});
  const [isContactorEnabled, setIsContactorEnabled] = useState(false);
  const [cbLoadData, setCBLoadData] = useState(null);
  const [currentProject, setCurrentProject] = useAtom(projectAtomList);
  const [defaultComponentData, setDefaultComponentData] =
    useAtom(projectAtomList);
  const [updateComponents, setUpdateComponents] = useAtom(projectAtomList);
  const [cbCount, setCbCount] = useAtom(projectAtomList);
  const [spanishCodeRealTime, setSpanishCodeRealTime] = useState(false);
  const [assetDefaultValues, setAssetDefaultValues] = useState();
  const [isAssetConfigChanged, setIsAssetConfigChanged] = useState(null);
  const [gtTempConfigData, setGtTempConfigData] = useState({});
  const projectStatusData = currentProject?.projectStatus;
  const [deploymentReady, setDeploymentReady] = useState(
    sessionStorage.getItem("deploymentReady")
  );

  // Reducer hooks
  const [ComponentData, ComponentDataDispatch] = useReducer(
    ComponentDataReducer,
    initialComponentData
  );
  const [CBAssetList, CBAssetListDispatch] = useReducer(
    CBAssetListReducer,
    initialCBAssetList
  );
  const [CBEVList, CBEVListDispatch] = useReducer(
    CBEVListReducer,
    initialCBEVList
  );
  const [CBLoadList, CBLoadListDispatch] = useReducer(
    CBLoadListReducer,
    initialCBLoadList
  );
  const [CBGTList, CBGTListDispatch] = useReducer(
    CBGTListReducer,
    initialCBGTList
  );
  const [firstTimeCall, setFirstTimecall] = useState(false);
  const [multipleLoads, setMultipleLoads] = useState(false);
  const [count, countDispatch] = useReducer(countReducer, initialCount);
  const [projectKind, projectKindDispatch] = useReducer(
    projectKindReducer,
    initialTypes
  );
  const [PVDetails, PVDetailsDispatch] = useReducer(
    PVDetailsReducer,
    initialPVDetails
  );
  const [selectedLoadIndx, setSelectedLoadIndx] = useState();

  // Ref hooks
  const projectId = props?.match?.params?.projectId || getProjectId();
  const initialRenderCoordinates = useRef(true);
  const initialRenderPvConfigured = useRef(true);
  const snackBarRef = useRef(null);
  const isUpdatingComponent = useRef(false);

  const [remoteMain, setRemoteMain] = useState(false);
  const [considerSelected, setConsiderSelected] = useState(false);
  const [assetDetails, setAssetDetails] = useState({
    assets: [],
    assetsData: [],
  });

  return {
    // Hooks
    projectTypeValue,
    dispatch,
    UserScope,
    projectType,

    // Basic state
    loading, setLoading,
    displayPage, setDisplayPage,
    sldLoadCompleted, setSldLoadCompleted,
    useCaseValidGenset, setUseCaseValidGenset,
    deleteOneDER, setDeleteOneDER,
    defaultConfigurationStatus, setDefaultConfigurationStatus,
    siteCreated, setSiteCreated,
    isOpen, setIsOpen,
    siteType, setSiteType,
    evceDiagramChangeRequired, setEvceDiagramChangeRequired,
    isAllowMultipleLoads, setIsAllowMultipleLoads,
    isExportOptimization, setIsExportOptimization,
    switchboardType, setSwitchboardType,
    clientType, setClientType,
    architectureType, setArchitectureType,
    nominalVoltage, setNominalVoltage,
    isSelectSpanishCode, setIsSelectSpanishCode,
    defaultPreFillData, setDefaultPreFillData,
    availableDer, setAvailableDer,
    useCases, setUseCases,
    isEvce, setIsEvce,
    prevPvGroupNames, setPrevPvGroupNames,

    // Communication
    batteryCommunicationData, setBatteryCommunicationData,
    evCommunicationData, setEvCommunicationData,
    powerMeter, setPowerMeter,
    communicationState, setCommunicationState,
    cbCommunicationValues, setCbCommunicationValues,

    // UI
    detailsBlock, setDetailsBlock,
    selectedValues, setSelectedValues,
    configData, setConfigData,
    isLoadNameUnique, setIsLoadNameUnique,
    gsxExternalId, setGsxExternalId,
    isBessController, setIsBessController,
    assetDetailsDisplay, setAssetDetailsDisplay,
    assetInfo, setAssetInfo,
    bessControllerPrevValue, setBessControllerPrevValue,
    cbDer, setCbder,
    CBValues, setCBValues,
    defaultsMinMax, setDefaultsMinMax,
    coordinates, setCoordinates,
    rectangleWidth, setRectangleWidth,
    snackBar, setSnackBar,
    alert, setAlert,
    confirm, setConfirm,
    remoteConfirm, setRemoteConfirm,
    duplicateUpdate, setDuplicateUpdate,
    runCalculateCoordinates, setRunCalculateCoordinates,
    runGenerateComponentsBlock, setRunGenerateComponentsBlock,
    batteryRack, setBatteryRack,
    batterySystemA2EConfig, setBatterySystemA2EConfig,
    isOpenLoadConfig, setIsOpenLoadConfig,
    isOpenGtConfig, setIsOpenGtConfig,
    loadSide, setLoadSide,
    isAntiIslandingRelayEnable, setIsAntiIslandingRelayEnable,
    gridTransitionOnline, setGridTransitionOnline,
    defaultCbData, setDefaultCbData,
    defaultPmData, setDefaultPmData,
    defaultDummyCbData, setDefaultDummyCbData,
    gensetAssetData, setGensetAssetData,
    evContactorData, setEVContactorData,
    gTContractorData, setGTContractorData,
    defaultGTData, setGTDefaultData,
    evDefaultData, setEVDefaultData,
    evPMData, setEVPMData,
    jsonData, setJsonData,
    isContactorEnabled, setIsContactorEnabled,
    cbLoadData, setCBLoadData,
    currentProject, setCurrentProject,
    defaultComponentData, setDefaultComponentData,
    updateComponents, setUpdateComponents,
    cbCount, setCbCount,
    spanishCodeRealTime, setSpanishCodeRealTime,
    assetDefaultValues, setAssetDefaultValues,
    isAssetConfigChanged, setIsAssetConfigChanged,
    gtTempConfigData, setGtTempConfigData,
    projectStatusData,
    deploymentReady, setDeploymentReady,

    // Reducers
    ComponentData, ComponentDataDispatch,
    CBAssetList, CBAssetListDispatch,
    CBEVList, CBEVListDispatch,
    CBLoadList, CBLoadListDispatch,
    CBGTList, CBGTListDispatch,
    firstTimeCall, setFirstTimecall,
    multipleLoads, setMultipleLoads,
    count, countDispatch,
    projectKind, projectKindDispatch,
    PVDetails, PVDetailsDispatch,
    selectedLoadIndx, setSelectedLoadIndx,

    // Refs
    projectId,
    initialRenderCoordinates,
    initialRenderPvConfigured,
    snackBarRef,
    isUpdatingComponent,

    // Other
    remoteMain, setRemoteMain,
    considerSelected, setConsiderSelected,
    assetDetails, setAssetDetails,
  };
};

export default useDesignAssetState;
